import { getCookieManagerService } from '../services/cookieManager';

const host = typeof window !== 'undefined' ? window.location?.host : '';

const isDynamicEnv = host.includes('-dynamic') && host.includes('.genial.ly');

const COOKIE_NAME = 'gen.lang';
const EXPIRES = 31536000;
const domain =
  // eslint-disable-next-line no-nested-ternary
  typeof document !== 'undefined' &&
  document.domain &&
  document.domain.indexOf('localhost') !== -1
    ? 'localhost'
    : isDynamicEnv
      ? '.genial.ly'
      : '.genially.com';

enum ShortLanguageCode {
  English = 'en',
  Spanish = 'es',
  French = 'fr',
  Brazilian = 'pt',
  Italian = 'it',
  German = 'de',
}

export enum Language {
  English = 'en',
  Spanish = 'es',
  French = 'fr',
  Brazilian = 'pt-br',
  Italian = 'it',
  German = 'de',
}

export enum LongLanguageCode {
  English = 'en-gb',
  Spanish = 'es-es',
  French = 'fr-fr',
  Brazilian = 'pt-br',
  Italian = 'it-it',
  German = 'de-de',
}

// TODO: shouldn't we migrate to use the Language enum above exclusively?
export enum LanguageKey {
  English = 'en',
  Spanish = 'es',
  French = 'fr',
  Brazilian = 'ptBr',
  Italian = 'it',
  German = 'de',
}

const languageToLanguageKeyMap: Record<Language, LanguageKey> = {
  [Language.English]: LanguageKey.English,
  [Language.Spanish]: LanguageKey.Spanish,
  [Language.French]: LanguageKey.French,
  [Language.Brazilian]: LanguageKey.Brazilian,
  [Language.Italian]: LanguageKey.Italian,
  [Language.German]: LanguageKey.German,
};

export const getLanguageKeyFromLanguage = (language: Language): LanguageKey => {
  return languageToLanguageKeyMap[language];
};

const shortCodeToLanguage: Record<ShortLanguageCode, Language> = {
  [ShortLanguageCode.English]: Language.English,
  [ShortLanguageCode.Spanish]: Language.Spanish,
  [ShortLanguageCode.French]: Language.French,
  [ShortLanguageCode.Brazilian]: Language.Brazilian,
  [ShortLanguageCode.Italian]: Language.Italian,
  [ShortLanguageCode.German]: Language.German,
};

export const DEFAULT_LANGUAGE = Language.English;

export const SUPPORTED_LANGUAGES = Object.values(Language);
export const SUPPORTED_LANGUAGE_SHORT_CODES = Object.values(ShortLanguageCode);

export const getResourceLanguage = (language: string) => {
  return language === Language.Spanish ? Language.Spanish : DEFAULT_LANGUAGE;
};

export function isSupportedLanguage(language: string): language is Language {
  return (SUPPORTED_LANGUAGES as string[]).includes(language);
}

export function isSupportedLanguageShortCode(
  language: string,
): language is ShortLanguageCode {
  return (SUPPORTED_LANGUAGE_SHORT_CODES as string[]).includes(language);
}

const standarizeBrowserLanguage = (browserLanguage: string): string => {
  if (isSupportedLanguage(browserLanguage)) {
    return browserLanguage;
  }

  const browserLanguageCode = browserLanguage.substring(0, 2);
  if (isSupportedLanguageShortCode(browserLanguageCode)) {
    return shortCodeToLanguage[browserLanguageCode];
  }

  return browserLanguage;
};

export const hasSavedLanguage = (): boolean => {
  const cookieManager = getCookieManagerService();
  const savedLanguage = cookieManager.read(COOKIE_NAME);
  return isSupportedLanguage(savedLanguage);
};

export const getSavedLanguage = (): Language => {
  const cookieManager = getCookieManagerService();
  const savedLanguage = cookieManager.read(COOKIE_NAME);
  return isSupportedLanguage(savedLanguage) ? savedLanguage : DEFAULT_LANGUAGE;
};

export const saveLanguage = (language: Language): void => {
  const cookieManager = getCookieManagerService();
  cookieManager.write(COOKIE_NAME, String(language), {
    domain,
    expires: EXPIRES,
  });
};

export const getBrowserLanguage = (restrictToLanguages?: Language[]): Language => {
  const nav = typeof navigator !== 'undefined' ? navigator : ({} as Navigator);
  const browserLanguage =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    nav?.language || ((nav as any)?.userLanguage as string | undefined) || '';
  const standarizedLanguage = standarizeBrowserLanguage(browserLanguage);
  const selectedLanguage = isSupportedLanguage(standarizedLanguage)
    ? standarizedLanguage
    : DEFAULT_LANGUAGE;

  if (Array.isArray(restrictToLanguages)) {
    return restrictToLanguages.includes(selectedLanguage)
      ? selectedLanguage
      : DEFAULT_LANGUAGE;
  }

  return selectedLanguage;
};

export const getSavedOrBrowserLanguage = (): Language =>
  hasSavedLanguage() ? getSavedLanguage() : getBrowserLanguage();
